import Grid from '@mui/material/Grid';
import { useEffect, useMemo, useState } from 'react';
import MyDatePicker from '../../components/MyDatePicker';
import MySelect from '../../components/MySelect';
import MySearchField from '../../components/MySearchField';
import { useBusinessUnitCodes } from '../../hooks/useBusinessUnitCodes';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useManagedRepTypes } from '../../hooks/useManagedRepTypes';
import { useSalesRegions } from '../../hooks/useSalesRegions';
import { useSalesTerritories } from '../../hooks/useSalesTerritories';

const activatedOptions = [
    { id: '', name: '-- CLEAR --' },
    { id: true, name: 'Yes' },
    { id: false, name: 'No' },
];

const FittingsFilter = ({ filter, filterChangeFunctions }) => {
    const [salesRegionOptions, setSalesRegionOptions] = useState([]);
    const [salesTerritoryOptions, setSalesTerritoryOptions] = useState([]);
    const [currentUser, _updateCurrentUser] = useCurrentUser(false);
    const [businessUnitCodes, setBusinessUnitCodes] = useState([]);
    useBusinessUnitCodes(true, null, (data) => {
        let options = [...data];
        if (options.length > 1) {
            options = [{ id: '', name: '-- CLEAR --' }, ...options];
        }
        if (options.length === 1) {
            filterChangeFunctions.businessUnitCode(options[0].id);
        }
        setBusinessUnitCodes(options);
    });
    const [managedRepTypes, setManagedRepTypes] = useState([]);
    useManagedRepTypes(currentUser?.id, true, null, setManagedRepTypes);

    const repTypeOptions = useMemo(() => {
        if (managedRepTypes.length === 0) {
            return [];
        }
        return [{ id: '', name: '-- CLEAR --' }, ...managedRepTypes];
    }, [managedRepTypes]);

    useSalesRegions(
        filter.businessUnitCode,
        currentUser.id,
        [1, 2].includes(currentUser.repType.id),
        (data) => {
            // Sort by repsCount desc
            data.sort((a, b) => b.repsCount - a.repsCount);

            // Then change name to name (repsCount)
            const updatedData = data.map((item) => ({
                ...item,
                name: `${item.name} (${item.repsCount} ${item.repsCount > 1 ? 'reps' : 'rep'})`,
            }));

            let options = [{ id: '', name: '-- CLEAR --' }, ...updatedData];
            setSalesRegionOptions(options);
        }
    );

    useSalesTerritories(
        filter.businessUnitCode,
        filter.salesRegion,
        [1, 2, 3].includes(currentUser.repType.id),
        (data) => {
            if (data.length > 0) {
                let options = [{ id: '', name: '-- CLEAR --' }, ...data];
                setSalesTerritoryOptions(options);
            } else {
                setSalesTerritoryOptions(data);
            }
        }
    );


    return (
        <Grid container rowSpacing={0.5} columnSpacing={1}>
            <Grid item sm={6} md={4} lg={3} xl={2.4}>
                <MySearchField
                    label="Search"
                    value={filter.searchString}
                    onChange={filterChangeFunctions.searchString}
                />
            </Grid>
            <Grid item sm={6} md={4} lg={3} xl={2.4}>
                <MyDatePicker
                    label="From Date"
                    variant="outlined"
                    fullWidth
                    value={filter.fromDate}
                    maxDate={filter.toDate || null}
                    onChange={filterChangeFunctions.fromDate}
                    sx={{
                        backgroundColor: 'white',
                    }}
                />
            </Grid>
            <Grid item sm={6} md={4} lg={3} xl={2.4}>
                <MyDatePicker
                    label="To Date"
                    variant="outlined"
                    fullWidth
                    value={filter.toDate}
                    minDate={filter.fromDate || null}
                    onChange={filterChangeFunctions.toDate}
                    sx={{
                        backgroundColor: 'white',
                    }}
                />
            </Grid>
            <Grid item sm={6} md={4} lg={3} xl={2.4}>
                <MySelect
                    label="Activated"
                    options={activatedOptions}
                    value={filter.active}
                    onChange={filterChangeFunctions.active}
                />
            </Grid>
            {currentUser.repType.id === 1 && (
                <Grid item sm={6} md={4} lg={3} xl={2.4}>
                    <MySelect
                        label="Business Unit"
                        options={businessUnitCodes}
                        value={filter.businessUnitCode}
                        onChange={(value) => {
                            filterChangeFunctions.businessUnitCode(value);
                        }}
                    />
                </Grid>
            )}
            {[1, 2].includes(currentUser.repType.id) && (
                <Grid item sm={6} md={4} lg={3} xl={2.4}>
                    <MySelect
                        label="Sales Region"
                        options={salesRegionOptions}
                        value={filter.salesRegion}
                        autoSelect={true}
                        onChange={(value) => {
                            filterChangeFunctions.salesRegion(value);
                        }}
                    />
                </Grid>
            )}
            {[1, 2, 3].includes(currentUser.repType.id) && (
                <Grid item sm={6} md={4} lg={3} xl={2.4}>
                    <MySelect
                        label="Sales Territory"
                        options={salesTerritoryOptions}
                        value={filter.salesTerritory}
                        onChange={filterChangeFunctions.salesTerritory}
                    />
                </Grid>
            )}
            {[1, 2, 3].includes(currentUser.repType.id) && (
                <Grid item sm={6} md={4} lg={3} xl={2.4}>
                    <MySelect
                        label="User Type"
                        options={repTypeOptions}
                        value={filter.repTypeId}
                        onChange={filterChangeFunctions.repTypeId}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default FittingsFilter;
